import styles from './styles.module.scss';
import React from 'react';
import icThc from 'assets/img/thetan_coin_V2.png';
import icBusd from 'assets/img/busd.png';
import icRefresh from 'assets/svg/ic_refresh_blue.svg';
import Button from 'src/Components/Button_V2';
import ErrorMessage from '../ErrorMessage';
import { usePrivateSale } from 'src/Pages/ProjectDetailPage/Components/ProjectPanel/PrivateSaleProvider';
import { useTypedDispatch, useTypedSelector } from 'src/redux/utils';
import { getThcExchangeRate } from 'src/redux/actions';
import { fetchIngameBalance, updateWalletBalance } from '@profile/src/redux/remoteActions';
import { CurrencyType, formatCurrency, normalNumberToSystemCurrency } from '@wolffun/system-currency';
import { Blockchain } from 'src/utils/types';
import { calculateMaxAllocationTicket, calculatePoolTicket, calculateReceivedToken, calculateThcFee, calculateUserTicket, checkUserError } from './utils';
import { AppError, EipError } from 'src/redux/types';
import { formatErrorMessage } from './utils';
import Checkbox from 'src/Components/CheckboxV2';
import useCheckAllowance from 'src/hooks/useCheckAllowance';
import { PRIVATE_SALE_CONTRACT_ADDRESS } from 'src/constants';
const INIT_BUSD = {
  token: CurrencyType.BUSD,
  value: '100000000'
};

// eslint-disable-next-line max-statements
const ConfirmationModal = ({
  projectDetail
}) => {
  const dispatch = useTypedDispatch();
  const ingameBalance = useTypedSelector(state => state.ingameBalance.data.balance);
  const busdBalance = useTypedSelector(state => state.connectWallet.busdBalance);
  const thcBalance = useTypedSelector(state => state.connectWallet.thcBalance);
  const thcExchangeRate = useTypedSelector(state => state.thcExchangeRate.data);
  const [amountTicket, setAmountTicket] = React.useState();
  const [amountBusd, setAmountBusd] = React.useState(0);
  const [thcFee, setThcFee] = React.useState(0);
  const [receivedToken, setReceivedToken] = React.useState(0);
  const [countDown, setCountDown] = React.useState(0);
  const [otherChainAddress, setOtherChainAddress] = React.useState();
  const minTicket = 1;
  const [error, setError] = React.useState(AppError.NO_ERROR);
  const [isExpired, setIsExpired] = React.useState(false);
  const [isConfirm, setIsConfirm] = React.useState(false);
  const isSolana = projectDetail.chainName === Blockchain.SOLANA;
  const isDisabled = amountTicket === undefined || amountTicket <= 0 || error !== AppError.NO_ERROR || isExpired || isSolana && !isConfirm || isSolana && !otherChainAddress;
  const ref = React.useRef(INIT_BUSD);
  const thc = React.useMemo(() => {
    return normalNumberToSystemCurrency(thcFee, CurrencyType.THC);
  }, [thcFee]);
  const {
    approveUseBalance,
    isEnoughAllowance,
    isProcessing
  } = useCheckAllowance({
    contractAddress: PRIVATE_SALE_CONTRACT_ADDRESS,
    thresholdPrice: thc
  });
  const {
    approveUseBalance: approveUseBalanceBusd,
    isEnoughAllowance: isEnoughAllowanceBusd,
    isProcessing: isProcessingBusd
  } = useCheckAllowance({
    contractAddress: PRIVATE_SALE_CONTRACT_ADDRESS,
    thresholdPrice: ref.current // fake value to trigger approve button

  });
  const poolTicket = React.useMemo(() => {
    const poolTicket = calculatePoolTicket(projectDetail.totalSale, projectDetail.totalSold, projectDetail.pTicketBusd);
    return poolTicket;
  }, [projectDetail.pTicketBusd, projectDetail.totalSale, projectDetail.totalSold]);
  const maxTicket = React.useMemo(() => {
    const maxAllocationTicket = calculateMaxAllocationTicket(projectDetail.maxAllocation, projectDetail.pTicketBusd);
    const userTicket = calculateUserTicket(ingameBalance.gPT);
    return Math.min(poolTicket, maxAllocationTicket, userTicket);
  }, [ingameBalance.gPT, poolTicket, projectDetail.maxAllocation, projectDetail.pTicketBusd]);
  React.useEffect(() => {
    if (amountTicket) {
      const userError = checkUserError({
        amountBusd,
        amountTicket,
        busdBalance,
        maxTicket,
        thcBalance,
        thcFee,
        minTicket
      });
      setError(userError);
    } else {
      setError(AppError.NO_ERROR);
    }
  }, [amountBusd, amountTicket, busdBalance, maxTicket, thcBalance, thcFee]);
  const getExchangeRate = React.useCallback(() => {
    dispatch(getThcExchangeRate());
  }, []);
  React.useEffect(() => {
    dispatch(updateWalletBalance());
    dispatch(fetchIngameBalance());
    getExchangeRate();
  }, []);
  React.useEffect(() => {
    if (!amountTicket) {
      return;
    }

    const busd = amountTicket * projectDetail.pTicketBusd;
    setAmountBusd(!isNaN(busd) ? busd : 0);
  }, [amountTicket, projectDetail.pTicketBusd]);
  React.useEffect(() => {
    const receivedTokenBN = calculateReceivedToken(amountBusd, projectDetail.pricePerToken);
    setReceivedToken(receivedTokenBN.toNumber());
    const thcFee = calculateThcFee(amountBusd, projectDetail.fee, thcExchangeRate.rate);
    setThcFee(!isNaN(thcFee) ? thcFee : 0);
  }, [amountBusd, projectDetail.fee, projectDetail.pricePerToken, projectDetail.pricePerToken.value, thcExchangeRate]);
  React.useEffect(() => {
    const interval = setInterval(() => {
      const remainSeconds = thcExchangeRate.expiredAt - Date.now() / 1000;

      if (Math.floor(remainSeconds) >= 0) {
        setCountDown(Math.floor(remainSeconds));
      } else {
        setIsExpired(true);
      }
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, [thcExchangeRate.expiredAt]);
  const {
    confirmOrder
  } = usePrivateSale();

  const renderPrimaryButton = () => {
    if (!isEnoughAllowance) {
      return /*#__PURE__*/React.createElement(Button, {
        disabled: isDisabled || isProcessing,
        className: styles.btn,
        onClick: async () => {
          const {
            error
          } = await approveUseBalance();

          if (error !== EipError.NO_ERROR) {
            setError(error);
          }
        },
        title: "Approve THC",
        type: "primary"
      });
    }

    if (!isEnoughAllowanceBusd) {
      return /*#__PURE__*/React.createElement(Button, {
        disabled: isDisabled || isProcessingBusd,
        className: styles.btn,
        onClick: async () => {
          const {
            error
          } = await approveUseBalanceBusd();

          if (error !== EipError.NO_ERROR) {
            setError(error);
          }
        },
        title: "Approve BUSD",
        type: "primary"
      });
    }

    return /*#__PURE__*/React.createElement(Button, {
      disabled: isDisabled,
      className: styles.btn,
      onClick: () => amountTicket && confirmOrder({
        pTicketAmount: normalNumberToSystemCurrency(amountTicket, CurrencyType.GPT),
        projectID: projectDetail.id,
        thcFee: normalNumberToSystemCurrency(thcFee, CurrencyType.THC),
        thcPrice: thcExchangeRate.rate,
        otherChainAddress,
        symbol: projectDetail.symbol ? projectDetail.symbol : 'TBA'
      }),
      title: "Confirm order",
      type: "primary"
    });
  };

  return /*#__PURE__*/React.createElement("div", {
    className: styles.modal
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.normalText
  }, "Private Ticket Amount"), /*#__PURE__*/React.createElement("div", {
    className: styles.wrapInput
  }, /*#__PURE__*/React.createElement("input", {
    value: amountTicket ? amountTicket.toString() : '',
    onChange: e => {
      const value = parseInt(e.target.value) || 0;
      const ACCEPTABLE_NUMBER = 1000000;

      if (value < ACCEPTABLE_NUMBER) {
        setAmountTicket(Math.abs(value));
      }
    },
    type: "number",
    placeholder: `Please enter ${minTicket} - ${maxTicket}`
  }), /*#__PURE__*/React.createElement("div", {
    className: styles.btnWrap
  }, /*#__PURE__*/React.createElement(Button, {
    className: styles.smallBtn,
    onClick: () => setAmountTicket(maxTicket),
    title: "Max",
    type: "primary"
  }), /*#__PURE__*/React.createElement("span", {
    className: styles.smallText
  }, "pTicket"))), /*#__PURE__*/React.createElement("span", {
    className: styles.note
  }, `Available Ticket: ${formatCurrency(ingameBalance.gPT, {
    withCurrency: false,
    trimWholeFraction: true
  })} Tickets`), /*#__PURE__*/React.createElement("span", {
    className: styles.normalText
  }, "Investment Amount"), /*#__PURE__*/React.createElement("div", {
    className: styles.richLabel
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.smallText
  }, amountBusd), /*#__PURE__*/React.createElement("div", {
    className: styles.labelIcon
  }, /*#__PURE__*/React.createElement("img", {
    src: icBusd,
    className: styles.icon
  }), /*#__PURE__*/React.createElement("span", {
    className: styles.smallText
  }, "BUSD"))), busdBalance && /*#__PURE__*/React.createElement("span", {
    className: styles.note
  }, `Your Balance: ${formatCurrency(busdBalance, {
    withCurrency: true,
    trimWholeFraction: true
  })}`), /*#__PURE__*/React.createElement("span", {
    className: styles.normalText
  }, `Investment  Fee (${projectDetail.fee}%)`), /*#__PURE__*/React.createElement("div", {
    className: styles.richLabel
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.smallText
  }, thcFee !== 0 ? thcFee.toLocaleString('en-US', {
    maximumFractionDigits: 2
  }) : 0), /*#__PURE__*/React.createElement("div", {
    className: styles.labelIcon
  }, /*#__PURE__*/React.createElement("img", {
    src: icThc,
    className: styles.icon
  }), /*#__PURE__*/React.createElement("span", {
    className: styles.smallText
  }, "THC"), /*#__PURE__*/React.createElement("img", {
    onClick: () => {
      getExchangeRate();
      setIsExpired(false);
    },
    className: styles.icRefresh,
    src: icRefresh
  }))), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("span", {
    className: styles.highlight
  }, `Fee reloads in ${countDown}s`), /*#__PURE__*/React.createElement("span", {
    className: styles.note
  }, `Your Balance: ${thcBalance ? formatCurrency(thcBalance, {
    withCurrency: true,
    trimWholeFraction: true
  }) : undefined}`)), /*#__PURE__*/React.createElement("span", {
    className: styles.normalText
  }, "Token Secured"), /*#__PURE__*/React.createElement("div", {
    className: styles.richLabel
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.smallText
  }, receivedToken), /*#__PURE__*/React.createElement("div", {
    className: styles.labelIcon
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.smallText
  }, projectDetail.symbol))), /*#__PURE__*/React.createElement("span", {
    className: styles.note
  }, `1 ${projectDetail.symbol} = ${formatCurrency(projectDetail.pricePerToken, {
    withCurrency: true,
    trimWholeFraction: true
  })}`), projectDetail.chainName === Blockchain.SOLANA && /*#__PURE__*/React.createElement("div", {
    className: styles.wallet
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.hr
  }), /*#__PURE__*/React.createElement("span", {
    className: styles.normalText
  }, "Your Wallet address"), /*#__PURE__*/React.createElement("input", {
    placeholder: "Please input your Solana wallet address to receive tokens",
    onChange: e => setOtherChainAddress(e.target.value),
    className: styles.input,
    type: "text"
  }), /*#__PURE__*/React.createElement("div", {
    className: styles.check,
    onClick: () => setIsConfirm(e => !e)
  }, /*#__PURE__*/React.createElement(Checkbox, {
    check: isConfirm,
    className: styles.checkbox
  }), /*#__PURE__*/React.createElement("span", {
    className: styles.label
  }, "I confirm the above Solana address is correct"))), isExpired ? /*#__PURE__*/React.createElement(ErrorMessage, {
    className: styles.message,
    description: 'THC rate expired. Please refresh for a new rate.'
  }) : error !== AppError.NO_ERROR && /*#__PURE__*/React.createElement(ErrorMessage, {
    className: styles.message,
    description: formatErrorMessage(error)
  }), renderPrimaryButton(), /*#__PURE__*/React.createElement("span", {
    className: styles.remind
  }, `You only have 1 chance to purchase each project.
      Seize the chance! The max investment amount is ${formatCurrency(projectDetail.maxAllocation, {
    withCurrency: false,
    trimWholeFraction: true
  })}$`));
};

export default ConfirmationModal;